import {
	Component,
	Input,
	OnInit
} from "@angular/core";
import { Router } from "@angular/router";
import { DateHelper } from "apps/idl/src/helpers";
import { EStatusSeance } from "apps/idl/src/model/EStatusSeance";
import { Traitement } from "apps/idl/src/model/Traitement";
import { IPatient } from "apps/idl/src/modules/patients/model/IPatient";
import { EMPTY, forkJoin, of } from "rxjs";
import { map, mergeMap, switchMap, takeUntil, tap } from "rxjs/operators";
import { TraitementService } from "../../../../../services/traitement.service";
import { StoredSeance } from "../../../../models/StoredSeance";
import { ConfirmationSuppressionComponent } from "../../../shared/components/confirmation-suppression/confirmation-suppression.component";
import { OrdonnanceStopComponent } from "../../../shared/components/ordonnance-stop/ordonnance-stop.component";
import { DeviceService } from "../../../shared/services/device.service";
import { DrawerPopoverService } from "../../../shared/services/drawer-popover.service";
import { PanneauService } from "../../../shared/services/panneau.service";
import { SeanceService } from "../../../shared/services/seance.service";
import { SnackbarService } from "../../../shared/services/snackbar.service";
import { DestroyableComponentBase } from "apps/idl/src/modules/utils/components/destroyable-component-base";

@Component({
	selector: "di-menu-ordonnance",
	templateUrl: "./menu-ordonnance.component.html",
	styleUrls: ["./menu-ordonnance.component.scss"],
})
export class MenuOrdonnanceComponent extends DestroyableComponentBase implements OnInit {

	@Input() ordonnance: Traitement;
	@Input() anchorElement?: any;
	@Input() edit?: boolean = true;
	@Input() hasSeanceFacturee: boolean = false
	@Input() patient?: IPatient;
	@Input() openChoix?: string;


	public showPopover: boolean = false;
	public isMobile: boolean = false;

	constructor(
		private svcTraitement: TraitementService,
		private svcDrawerPopover: DrawerPopoverService,
		private svcSeance: SeanceService,
		private router: Router,
		private svcSnackbar: SnackbarService,
		private svcDevice: DeviceService,
		private svcPanneau: PanneauService
	) {
		super();
	}

	ngOnInit() {
		this.svcDevice.isMobile$.pipe(takeUntil(this.destroyed$)).subscribe((flag: boolean) => {
			this.isMobile = flag;
		});

		if (this.openChoix) {
			this.executerOpenChoix();
		}
	}

	public executerOpenChoix(): void {
		this.closeMenu();

		switch (this.openChoix) {
			case "patient":
				this.handleDossierPatient();
				break;
		}
	}

	handleConfirmSuppression(ordonnance: Traitement) {
		this.svcDrawerPopover.close();

		// On regarde si l'ordonnance a des séances facturées avant de supprimer
		this.svcSeance.selectSeancesByTraitement(ordonnance._id,true).pipe(
			map((seances: StoredSeance[]) => seances.some((seance: StoredSeance) => seance.status === EStatusSeance.completed)),
			mergeMap((hasCompletedSeances: boolean) => {
				if (!hasCompletedSeances) {
					return this.svcTraitement.deleteTraitement(ordonnance, true).pipe(
						mergeMap(() => this.svcSeance.selectSeancesByTraitement(this.ordonnance._id)),
						mergeMap((listSeance: StoredSeance[]) => {
							if (listSeance.length === 0) {
								return of(null);
							}
							const deleteSeanceObservables = listSeance.map(seance => this.svcSeance.deleteSeance(seance));
							return forkJoin(deleteSeanceObservables);
						})
					)
				} else {
					// On ne supprime pas l'ordonnance, donc la liste des ordonnances n'a pas besoin d'être actualisée
					this.svcSnackbar.showToast(
						"error",
						"bottom center",
						"L'ordonnance ne peut pas être supprimée car des séances ont déjà été facturées",
						null,
						null,
						null,
						"7000"
					);
					return EMPTY;
				}
			})
		).subscribe({
			next: () => {
				// Emit pour que la liste des ordonnances soit actualisée
				this.svcTraitement.emitDeleteTraitement();
				this.router.navigate(["ordonnances"]);
			},
			error: (error) => {
				console.error('Erreur lors de la suppression :', error);
			}
		})
	}

	public closeMenu(): void {
		this.showPopover = false;
		this.svcDrawerPopover.close();
	}

	handleRenouvelerClick() {
		if (!this.patient) return;
		this.svcDrawerPopover.close();
		let state = { route: ['ordonnances', 'add'], context: 'ordonnance', mode: 'add', ordonnanceOrigineId: this.ordonnance._id };
		this.router.navigate(["ordonnances", "add"], { state: state });
	}

	handleFacturationClick(event: Event) {
		if (!this.patient) return;
		this.closeMenu()
		this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
			this.router.navigate(["facturation/controle", this.ordonnance._id])
		});
	}

	handleConfirmStop(ordonnance: Traitement) {
		if (!this.patient) return;

		this.svcSeance.selectSeancesByTraitement(ordonnance._id).pipe(
			map((seances: StoredSeance[] | null | undefined) =>
				(seances || []).filter((seance: StoredSeance) =>
					DateHelper.compareTwoDates(seance.startDate, this.ordonnance.stopDate) > 0
				)
			),
			switchMap((seancesFutures: StoredSeance[]) =>
				seancesFutures.length > 0
					? forkJoin(seancesFutures.map((seance: StoredSeance) => this.svcSeance.deleteSeance(seance)))
					: of([])
			),
			switchMap(() => this.svcTraitement.saveTraitementANAKIN(ordonnance)),
			tap(() => this.svcDrawerPopover.close())
		).subscribe();
	}

	handleStopClick(event: MouseEvent) {
		if(!this.patient) return;
		const element: HTMLElement = event.currentTarget as HTMLElement;
		const anchorRect: DOMRect = element.getBoundingClientRect();
		this.showPopover = true;
		this.svcDrawerPopover.open(
			"Arrêter les soins",
			"50%",
			this.anchorElement?.nativeElement,
			OrdonnanceStopComponent,
			{
				ordonnance: this.ordonnance,
				confirm: (ordonnance) => {
					this.handleConfirmStop(ordonnance);
				},
				cancel: () => this.closeMenu(),
			},
			() => (this.showPopover = false),
			anchorRect
		);
	}

	handleSupprimerClick(event: MouseEvent) {
		// On ne peut pas supprimer d'ordonnance qui ont au moins une séance facturée
		if (this.hasSeanceFacturee) {
			return;
		}
		this.showPopover = true;
		this.svcDrawerPopover.open(
			"Confirmation de suppression",
			"50%",
			this.anchorElement?.nativeElement,
			ConfirmationSuppressionComponent,
			{
				onConfirm: () => this.handleConfirmSuppression(this.ordonnance),
				onCancel: () => this.closeMenu(),
			},
			() => (this.showPopover = false)
		);
	}

	handleEditClick(event: MouseEvent) {
		this.svcDrawerPopover.close();
		this.router.navigate(["ordonnances", "edit", this.ordonnance._id]);
	}

	public handleDossierPatient(): void {
		if (!this.patient) return;
		this.closeMenu()
		this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
			this.router.navigate(["patients", this.patient._id], { state: { tabSelected: "Informations" } })
		});
	}
}
